import React, { useState, useEffect } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  CircularProgress,
  styled,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import Send from '@mui/icons-material/Send'
import LocalHospital from '@mui/icons-material/LocalHospital'
import { default as AssessmentIcon } from '@mui/icons-material/Assessment'
import { toast } from 'react-toastify'
import ModernSendTherapyComponent from 'src/clinic/components/ClinicManageBookingsPage/sendPrescriptionData/components/ModernSendTherapyComponent'
import ModernSendAssessmentComponent from 'src/clinic/components/ClinicManageBookingsPage/sendPrescriptionData/components/ModernSendAssessmentComponent'
import { HE_KEY_DIAGNOSIS, HE_KEY_PSYCH_ASSESSMENTS } from 'src/consumer/constants'
import { removeKeyFromObject } from 'src/consumer/helpers/utilFunctions'
import {
  getHealthEntityArrayForSubmission,
  getInitialDataForSettingDaysOfNextVisit,
  getInitialDataForSettingExistingHealthEntity,
  getInitialDataForSettingExistingSelectedTherapies,
  getNextVisitDateForSubmission,
  getSelectedTherapiesArrayForSubmission,
  saveNewHealthEntityList,
} from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import { getMPDataWithoutParams, postMPDataWithoutParams } from 'src/mentor/services'
import { getIpAddressFromLocalStorage } from 'src/consumer/services/localstorageServices'
import { getIpAddress } from 'src/helper'

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: '24px',
  borderBottom: '2px solid rgba(0, 0, 0, 0.08)',
  '& .MuiTabs-indicator': {
    height: '3px',
    borderRadius: '3px 3px 0 0',
    background: 'linear-gradient(90deg, #4338ca 0%, #6366f1 100%)',
  },
}))

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '15px',
  fontWeight: 500,
  color: 'rgba(0, 0, 0, 0.6)',
  minHeight: '60px',
  padding: '12px 24px',
  '&.Mui-selected': {
    color: '#4338ca',
    fontWeight: 600,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '22px',
  },
}))

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#fff',
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.9) 0%, rgba(99, 102, 241, 0.9) 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
  },
  '&.Mui-disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
}))

const REFERRAL_TABS = {
  THERAPY: 'therapy',
  ASSESSMENT: 'assessment',
}

const HE_KEY_THERAPY_TYPE = 'therapy-type'

export default function SendForReferralContent({
  bookedEventInfo,
  refreshBookedSlots,
  mentorProfileData,
  prescriptionGetApiSignature = '/api/secure/doctor/prescription/get-by-eventId',
  prescriptionSaveApiSignature = '/api/secure/doctor/prescription/save',
  showGallery = false,
  galleryImagesSearch,
  fileUploadApiSignature = '/api/secure/doctor/file/upload',
}) {
  const [currentTab, setCurrentTab] = useState(REFERRAL_TABS.THERAPY)
  const [loading, setLoading] = useState(false)
  const [prescriptionSaveLoading, setPrescriptionSaveLoading] = useState(false)
  
  const clinicId = bookedEventInfo?.clinicId
  const clinicBrandId = bookedEventInfo?.clinicBrandId

  // Shared state
  const [prescriptionData, setPrescriptionData] = useState()
  const [selectedDiagnosis, setSelectedDiagnosis] = useState([])
  const [nextVisitInputValue, setNextVisitInputValue] = useState(null)
  const [nextVisitInputType, setNextVisitInputType] = useState('days')
  const [uploadPrescriptionImages, setUploadPrescriptionImages] = useState([])

  // Therapy-specific state
  const [selectedTherapies, setSelectedTherapies] = useState([])
  const [notesToTherapist, setNotesToTherapist] = useState('')
  const [attachPrescriptionForTherapy, setAttachPrescriptionForTherapy] = useState(false)

  // Assessment-specific state
  const [selectedAssessments, setSelectedAssessments] = useState([])
  const [notesForAssessments, setNotesForAssessments] = useState('')
  const [attachPrescriptionForAssessment, setAttachPrescriptionForAssessment] = useState(false)
  const [markSessionCompleted, setMarkSessionCompleted] = useState(false)

  async function setup() {
    setLoading(true)
    let metaPrescription = null
    
    try {
      const response = await getMPDataWithoutParams(
        `${prescriptionGetApiSignature}?eventId=${bookedEventInfo?.id}`,
      )
      if (response.status === 200) {
        metaPrescription = response?.data?.data?.metaPrescription
      }
    } catch (error) {
      console.error('Error fetching prescription data:', error)
      // Continue with empty form if no prescription data exists
    }

    // Store the prescription metadata for updating
    setPrescriptionData(metaPrescription)

    // Initialize all fields from prescription data if it exists, otherwise start with empty/default values
    const prescData = metaPrescription?.prescriptionData

    // Diagnosis - always initialize
    setSelectedDiagnosis(
      prescData?.diagnosis 
        ? getInitialDataForSettingExistingHealthEntity(prescData.diagnosis)
        : []
    )

    // Next Visit - initialize if exists
    if (prescData?.nextVisitDate) {
      setNextVisitInputValue(
        getInitialDataForSettingDaysOfNextVisit(
          prescData.nextVisitDate,
          prescData.createdTime,
          prescData.nextVisitTypeToShow || 'days',
        ),
      )
    } else {
      setNextVisitInputValue(null)
    }

    // Next Visit Type
    setNextVisitInputType(prescData?.nextVisitTypeToShow || 'days')

    // Uploaded Images
    setUploadPrescriptionImages(prescData?.uploadedImages || [])

    // Notes to Therapist
    setNotesToTherapist(prescData?.notesToTherapist || '')

    // Notes for Assessments
    setNotesForAssessments(prescData?.notesForAssessment || prescData?.notesForAssessments || '')

    // Therapies Needed
    setSelectedTherapies(
      prescData?.therapiesNeeded
        ? getInitialDataForSettingExistingSelectedTherapies(prescData.therapiesNeeded)
        : []
    )

    // Psychological Assessments
    setSelectedAssessments(
      prescData?.psychAssessments
        ? getInitialDataForSettingExistingHealthEntity(prescData.psychAssessments)
        : []
    )

    // Attach Prescription checkboxes
    setAttachPrescriptionForTherapy(prescData?.attachPrescriptionForTherapy || false)
    setAttachPrescriptionForAssessment(prescData?.attachPrescriptionForAssessment || false)

    setLoading(false)
  }

  async function savePrescription(referralType) {
    setPrescriptionSaveLoading(true)
    const newlySavedItems = {}
    const isTherapy = referralType === REFERRAL_TABS.THERAPY
    const newDiagnosisToSave = selectedDiagnosis?.filter((d) => !!d.label)
    const newDiagnosis = await saveNewHealthEntities(newDiagnosisToSave, HE_KEY_DIAGNOSIS)

    const newPsychAssessments = await saveNewHealthEntities(
      selectedAssessments,
      HE_KEY_PSYCH_ASSESSMENTS,
    )
    const newTherapyTypes = await saveNewHealthEntities(
      selectedTherapies,
      HE_KEY_THERAPY_TYPE,
    )

    newlySavedItems[HE_KEY_DIAGNOSIS] = newDiagnosis
    newlySavedItems[HE_KEY_PSYCH_ASSESSMENTS] = newPsychAssessments
    newlySavedItems[HE_KEY_THERAPY_TYPE] = newTherapyTypes

    async function saveNewHealthEntities(data, healthEntity, isApplyingCompleteObject = false) {
      return await saveNewHealthEntityList(
        data,
        healthEntity,
        clinicId,
        clinicBrandId,
        isApplyingCompleteObject,
      )
    }

    // Get mentor IP address
    let mentorIP = getIpAddressFromLocalStorage()
    if (!mentorIP) {
      // getIpAddress returns null on error, so no need for try-catch
      mentorIP = await getIpAddress() || undefined
    }

    // Get mentor name
    const mentorName =
      mentorProfileData?.name ||
      mentorProfileData?.firstName ||
      localStorage.getItem('userName') ||
      bookedEventInfo?.mentorName

    // Preserve all existing prescription data and update with current form values
    let prescriptionData_toSave = {
      ...removeKeyFromObject(prescriptionData?.prescriptionData || {}),
      mentorId: bookedEventInfo?.mentorId,
      patientId: bookedEventInfo?.patientId,
      bookedEventId: bookedEventInfo?.id,
      prescriptionId: prescriptionData?.prescriptionData?.prescriptionId
        ? prescriptionData?.prescriptionData?.prescriptionId
        : '',
      // Shared fields - always update from current form
      diagnosis: getHealthEntityArrayForSubmission(
        newDiagnosisToSave,
        HE_KEY_DIAGNOSIS,
        clinicId,
        clinicBrandId,
        newlySavedItems,
      ),
      psychAssessments: getHealthEntityArrayForSubmission(
        selectedAssessments,
        HE_KEY_PSYCH_ASSESSMENTS,
        clinicId,
        clinicBrandId,
        newlySavedItems,
      ),
      uploadedImages: uploadPrescriptionImages,
      therapiesNeeded: getSelectedTherapiesArrayForSubmission(selectedTherapies),
      nextVisitDate: getNextVisitDateForSubmission(nextVisitInputValue, nextVisitInputType),
      nextVisitTypeToShow: nextVisitInputType,
      // Therapy-specific fields - update only if saving therapy, otherwise preserve existing
      suggestedBookingTherapy: isTherapy
        ? true
        : (prescriptionData?.prescriptionData?.suggestedBookingTherapy || false),
      notesToTherapist: notesToTherapist || prescriptionData?.prescriptionData?.notesToTherapist || '',
      attachPrescriptionForTherapy: attachPrescriptionForTherapy,
      // Assessment-specific fields - preserve regardless of which referral type is being saved
      notesForAssessment: notesForAssessments || prescriptionData?.prescriptionData?.notesForAssessment || '',
      attachPrescriptionForAssessment: attachPrescriptionForAssessment,
      // Metadata
      templateId: prescriptionData?.id,
      qrCodeImageId: prescriptionData?.prescriptionData?.qrCodeImageId,
      prescriptionLanguages: ['en'],
      mentorName: mentorName,
    }

    // Only add mentorIP if it's available
    if (mentorIP) {
      prescriptionData_toSave.mentorIP = mentorIP
    }

    // Wrap prescription data in EmrReportPayload structure
    const body = {
      prescription: prescriptionData_toSave,
      emrSectionDataList: [],
      screeningReport: null,
      therapyReport: null,
      assessmentReport: null,
      triggerTherapyReferral: isTherapy,
      triggerAssessmentReferral: !isTherapy,
      markSessionInProgress: true,
      markSessionCompleted,
    }

    const response = await postMPDataWithoutParams(prescriptionSaveApiSignature, body)
    if (response.status === 200) {
      if (refreshBookedSlots) {
        refreshBookedSlots()
      }
      toast.success(
        isTherapy
          ? 'Therapy referral sent successfully'
          : 'Assessment referral sent successfully',
      )
    } else {
      toast.error('Something went wrong.')
    }
    setPrescriptionSaveLoading(false)
  }

  useEffect(() => {
    setup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue)
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 4,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Tabs */}
      <StyledTabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
        <StyledTab
          value={REFERRAL_TABS.THERAPY}
          label="Therapy Referral"
          icon={<LocalHospital />}
          iconPosition="start"
        />
        <StyledTab
          value={REFERRAL_TABS.ASSESSMENT}
          label="Assessment Referral"
          icon={<AssessmentIcon />}
          iconPosition="start"
        />
      </StyledTabs>

      {/* Tab Content */}
      <Box 
        sx={{ 
          minHeight: '300px', 
          maxHeight: { xs: 'calc(92vh - 300px)', md: 'calc(100vh - 280px)' }, 
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: { xs: 0.5, md: 1 },
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(67, 56, 202, 0.3)',
            borderRadius: '10px',
            '&:hover': {
              background: 'rgba(67, 56, 202, 0.5)',
            },
          },
        }}
      >
        {currentTab === REFERRAL_TABS.THERAPY ? (
          <ModernSendTherapyComponent
            clinicId={clinicId}
            clinicBrandId={clinicBrandId}
            selectedDiagnosis={selectedDiagnosis}
            setSelectedDiagnosis={setSelectedDiagnosis}
            nextVisitInputValue={nextVisitInputValue}
            setNextVisitInputValue={setNextVisitInputValue}
            nextVisitInputType={nextVisitInputType}
            setNextVisitInputType={setNextVisitInputType}
            notesToTherapist={notesToTherapist}
            setNotesToTherapist={setNotesToTherapist}
            bookedEventInfo={bookedEventInfo}
            setUploadPrescriptionImages={setUploadPrescriptionImages}
            uploadPrescriptionImages={uploadPrescriptionImages}
            loading={loading}
            setLoading={setLoading}
            selectedTherapies={selectedTherapies}
            setSelectedTherapies={setSelectedTherapies}
            attachPrescriptionForTherapy={attachPrescriptionForTherapy}
            setAttachPrescriptionForTherapy={setAttachPrescriptionForTherapy}
            showGallery={showGallery}
            mentorUserId={mentorProfileData?.userId}
            galleryImagesSearch={galleryImagesSearch}
            fileUploadApiSignature={fileUploadApiSignature}
          />
        ) : (
          <ModernSendAssessmentComponent
            clinicId={clinicId}
            clinicBrandId={clinicBrandId}
            selectedDiagnosis={selectedDiagnosis}
            setSelectedDiagnosis={setSelectedDiagnosis}
            selectedAssessments={selectedAssessments}
            setSelectedAssessments={setSelectedAssessments}
            notesForAssessments={notesForAssessments}
            setNotesForAssessments={setNotesForAssessments}
            attachPrescriptionForAssessment={attachPrescriptionForAssessment}
            setAttachPrescriptionForAssessment={setAttachPrescriptionForAssessment}
            loading={loading}
            setLoading={setLoading}
            mentorProfileData={mentorProfileData}
          />
        )}
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          marginTop: 'auto',
          paddingTop: 2,
          paddingBottom: { xs: 2, md: 1 },
          paddingX: { xs: 0, md: 0 },
          borderTop: '2px solid rgba(67, 56, 202, 0.1)',
          background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.98) 70%, rgba(255,255,255,0.9) 100%)',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
          zIndex: 10,
        }}
      >
        <FormControlLabel
          sx={{
            alignItems: 'flex-start',
            marginBottom: 1.5,
            marginLeft: 0,
            '& .MuiFormControlLabel-label': {
              width: '100%',
            },
          }}
          control={
            <Checkbox
              checked={markSessionCompleted}
              onChange={(event) => setMarkSessionCompleted(event.target.checked)}
              sx={{
                color: '#4338ca',
                paddingTop: 0.25,
                '&.Mui-checked': {
                  color: '#4338ca',
                },
              }}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#4338ca' }}>
                Mark session completed
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                Leave unchecked to move the session to in-progress.
              </Typography>
            </Box>
          }
        />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'flex-end',
            alignItems: 'stretch',
          }}
        >
          <StyledButton
            variant="contained"
            onClick={() => savePrescription(currentTab)}
            disabled={prescriptionSaveLoading}
            startIcon={
              prescriptionSaveLoading ? <CircularProgress size={20} color="inherit" /> : <Send />
            }
            sx={{
              flex: { xs: 1, sm: 'initial' },
              minWidth: { sm: '200px' },
            }}
          >
            {prescriptionSaveLoading
              ? 'Sending...'
              : currentTab === REFERRAL_TABS.THERAPY
              ? 'Send for Therapy'
              : 'Send for Assessment'}
          </StyledButton>
        </Box>
      </Box>
    </Box>
  )
}
